'use client'

/**
 * A single polite live region for transient confirmations (T-WEB-08, T-A11Y-01).
 *
 * `aria-live="polite"` rather than `assertive`: a "Saved" confirmation must
 * not interrupt a screen reader mid-sentence. The region is rendered
 * unconditionally and only its children change — a region that appears at
 * the same moment as its text is frequently missed by assistive tech.
 */
import {createContext, useCallback, useContext, useMemo, useState} from 'react'
import './shell/shell.css'

type Tone = 'info' | 'error'

interface Toast {
  id: number
  text: string
  tone: Tone
}

interface ToastApi {
  show: (text: string, tone?: Tone) => void
}

const ToastContext = createContext<ToastApi>({show: () => {}})

export function useToast(): ToastApi {
  return useContext(ToastContext)
}

const VISIBLE_MS = 5000

export function ToastProvider({children}: {children: React.ReactNode}) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const show = useCallback((text: string, tone: Tone = 'info') => {
    const id = Date.now() + Math.random()
    setToasts((current) => [...current, {id, text, tone}])
    setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id))
    }, VISIBLE_MS)
  }, [])

  const api = useMemo(() => ({show}), [show])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="u-toasts" role="status" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <p key={toast.id} className={`u-toast${toast.tone === 'error' ? ' u-toast--error' : ''}`}>
            {toast.text}
          </p>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
